/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Avatar from "react-avatar";
import { UpdateProfile } from "../../core/_requests";
import { useAuth } from "../../../../auth";
import { toast } from "react-toastify";
import Cropper from "react-easy-crop";
import { Modal } from "react-bootstrap";
import getCroppedImg from "../../../../component/core/cropImageUtils";// your crop util

interface ProfileProps {
  listProfile: any;
}

const ProfileSettings: React.FC<ProfileProps> = ({ listProfile }) => {
  const { currentUser } = useAuth();
  const chefId = currentUser?.uuid || "";
  const navigate = useNavigate();

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setImagePreview(listProfile?.profile || null);
  }, [listProfile]);

  const onImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!file) return;

    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Only JPEG, PNG, and JPG allowed.");
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error("File size must be less than 1MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        setCropImage(reader.result as string);
        setCropModalOpen(true);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async () => {
    if (!cropImage || !croppedAreaPixels) return;
    try {
      const croppedBlob = await getCroppedImg(cropImage, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], "profile.jpg", {
        type: "image/jpeg",
      });

      await uploadProfileImage(croppedFile);
      setImagePreview(URL.createObjectURL(croppedBlob));
      setCropModalOpen(false);
    } catch (err) {
      console.error("Crop error:", err);
      toast.error("Crop failed");
    }
  };

  const uploadProfileImage = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("profile", file);
      const res = await UpdateProfile(formData, chefId);
      if (res?.status === true) {
        toast.success("Profile updated!");
        navigate("/settings/profile-success", { replace: true });
      } else {
        toast.error("Failed to update profile.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Something went wrong.");
    }
  };

  return (
    <div>
      <div className="profile-pic-wrap">
        <div className="pic-wrap">
          <span className="txt">
            <Avatar
              src={imagePreview || undefined}
              name={currentUser?.data?.name || listProfile?.name}
              size="125"
              round
              maxInitials={1}
            />
          </span>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg, image/png, image/jpg"
          style={{ display: "none" }}
          onChange={onImageSelect}
        />
        <button
          className="btn btn-primary btn-circle"
          onClick={() => fileRef.current?.click()}
        >
          <i className="material-symbols-outlined">add_photo_alternate</i>
        </button>
      </div>

      <div className="personal-detail mt-4 mb-3">
        <div>
          <h3 className="fw-med">{listProfile?.name}</h3>
          <ul className="list-inline mt-3">
            <li className="list-inline-item">
              <i className="material-symbols-outlined call">call</i>
              {listProfile?.mobileNumber}
            </li>
            <li className="list-inline-item">
              <i className="material-symbols-outlined">mail</i>
              {listProfile?.email}
            </li>
            <li className="list-inline-item w-100 mt-1">
              <i className="material-symbols-outlined">location_on</i>
              {listProfile?.address}
            </li>
          </ul>
        </div>
        <a
          href="#"
          className="text-link"
          onClick={() => navigate("/settings/profile-details")}
        >
          Edit
        </a>
      </div>

      {/* Crop Modal */}
  <Modal
  show={cropModalOpen}
  onHide={() => setCropModalOpen(false)}
  size="lg"
  centered
  scrollable
  className="crop-modal"
>
  <Modal.Header closeButton>
    <Modal.Title>Crop Image</Modal.Title>
  </Modal.Header>

  <Modal.Body className="p-0 crop-body">
    {cropImage && (
      <div className="crop-container">
        <Cropper
          image={cropImage}
          crop={crop}
          zoom={zoom}
          aspect={1}
          cropShape="round"
          showGrid={false}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
        />
      </div>
    )}
  </Modal.Body>

  <Modal.Footer className="d-flex justify-content-between flex-wrap">
    <button className="btn btn-secondary" onClick={() => setCropModalOpen(false)}>
      Cancel
    </button>
    <button className="btn btn-primary" onClick={handleCropComplete}>
      Crop & Save
    </button>
  </Modal.Footer>
</Modal>

    </div>
  );
};

export default ProfileSettings;
